import { useState } from "react";
import { LuRotateCw } from "react-icons/lu";
import { NAME_FIELD_REGEX, PHONE_REGEX } from "@/constants";
import { validateDob } from "@/utils/validateField/dob";
import {
  FIRST_NAME_ERROR_MESSAGE,
  SECOND_NAME_ERROR_MESSAGE,
  PHONE_ERROR_MESSAGE,
} from "@/pages/quiz/base/questions/contactField/common";
import { SwitchStateLink } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/switchStateLink";
import { VerifyButton } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/components/verifyButton";
import { VerifyTextInput } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/components/verifyTextInput";
import { SsnDigitsInput } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/components/ssnDigitsInput";
import {
  IdentityChip,
  ErrorBanner,
  EncryptedNote,
} from "@/pages/quiz/base/questions/sumsubAttachmentBlock/states/components/verifyChrome";
import {
  MismatchInfo,
  MismatchReason,
} from "@/pages/quiz/base/questions/sumsubAttachmentBlock/types";
import { VerifyParams } from "@/pages/quiz/base/questions/sumsubAttachmentBlock/useVerifySsn";

const NAME_MIN_LENGTH = 2;
const NAME_MAX_LENGTH = 50;
const MISMATCH_MESSAGE =
  "These details don’t match the records in the SSN database. Review your information below and try again.";
const INVALID_PHONE_TITLE = "We couldn’t verify this phone number";
const INVALID_PHONE_MESSAGE =
  "We couldn’t verify the phone number you entered. Please enter a valid phone number and try again.";

const validateName = (value: string, isFirstName: boolean) => {
  const trimmed = value.trim();
  const failMessage = isFirstName
    ? FIRST_NAME_ERROR_MESSAGE
    : SECOND_NAME_ERROR_MESSAGE;

  if (!trimmed) return failMessage;
  if (
    trimmed.length < NAME_MIN_LENGTH ||
    trimmed.length > NAME_MAX_LENGTH ||
    !NAME_FIELD_REGEX.test(trimmed)
  ) {
    return failMessage;
  }

  return "";
};

const validatePhone = (value: string) => {
  const trimmed = value.trim();
  if (!trimmed) return PHONE_ERROR_MESSAGE;
  return PHONE_REGEX.test(trimmed) ? "" : PHONE_ERROR_MESSAGE;
};

// "1990-01-31" -> "01/31/1990" (the format the dob input mask renders)
const isoToDisplay = (iso?: string) => {
  const match = iso?.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  const [, year, month, day] = match;
  return `${month}/${day}/${year}`;
};

// "01/31/1990" -> "1990-01-31"
const displayToIso = (display: string) => {
  const digits = display.replace(/\D/g, "");
  if (digits.length !== 8) return "";
  return `${digits.slice(4, 8)}-${digits.slice(0, 2)}-${digits.slice(2, 4)}`;
};

// "+12065551111" -> "(206) 555-1111" for the phone input mask
const phoneToDisplay = (raw?: string) => {
  const digits = (raw ?? "").replace(/\D/g, "");
  const ten =
    digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
  if (ten.length !== 10) return "";
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
};

interface IProps {
  info: MismatchInfo;
  isLoading: boolean;
  verifyError?: string;
  ssnValue: string;
  onSsnChange: (value: string) => void;
  // Correction overrides for the shown fields (order uuid + ssn added by parent).
  onSubmit: (corrections: Omit<VerifyParams, "ssnLastFour">) => void;
  onUploadInstead: () => void;
  onScrollToTop: () => void;
}

const MismatchState = ({
  info,
  isLoading,
  verifyError,
  ssnValue,
  onSsnChange,
  onSubmit,
  onUploadInstead,
  onScrollToTop,
}: IProps) => {
  const [firstName, setFirstName] = useState(info.submitted?.first_name ?? "");
  const [lastName, setLastName] = useState(info.submitted?.last_name ?? "");
  const [dob, setDob] = useState(isoToDisplay(info.submitted?.date_of_birth));
  const [phone, setPhone] = useState(phoneToDisplay(info.submitted?.phone));

  // Field-level errors (string message or false). Not shown on load.
  const [firstNameError, setFirstNameError] = useState<string | boolean>(false);
  const [lastNameError, setLastNameError] = useState<string | boolean>(false);
  const [dobError, setDobError] = useState<string | boolean>(false);
  const [phoneError, setPhoneError] = useState<string | boolean>(false);

  const isPhoneRejected = info.reason === MismatchReason.INVALID_PHONE;
  // The rejected number is well formed, so validatePhone can’t flag it. Reset
  // on submit: only a new result can tell us whether the number is usable.
  const [isPhoneRetyped, setIsPhoneRetyped] = useState(false);

  // Validity computed from the values (not just touched errors) so the button
  // stays disabled whenever any field is invalid.
  const canSubmit =
    validateName(firstName, true) === "" &&
    validateName(lastName, false) === "" &&
    validateDob(dob) === "" &&
    validatePhone(phone) === "" &&
    ssnValue.length === 4;

  // Run blur/submit validation; returns true when all fields are valid.
  const runValidation = () => {
    const firstError = validateName(firstName, true);
    const lastError = validateName(lastName, false);
    const dateError = validateDob(dob);
    const phoneErr = validatePhone(phone);

    setFirstNameError(firstError || false);
    setLastNameError(lastError || false);
    setDobError(dateError || false);
    setPhoneError(phoneErr || false);

    return !firstError && !lastError && !dateError && !phoneErr;
  };

  const handleTryAgain = () => {
    // Bring the header/error into view even when validation blocks the submit.
    onScrollToTop();
    if (!runValidation()) return;

    setIsPhoneRetyped(false);
    onSubmit({
      firstName: firstName.trim(),
      lastName: lastName.trim(),
      dateOfBirth: displayToIso(dob),
      phoneNumber: phone.replace(/\D/g, ""),
    });
  };

  return (
    <>
      <IdentityChip />

      <h1 className="mb-3 text-[28px] font-semibold leading-tight text-[#2c2150] sm:text-3xl">
        Verify it’s you to finish your order
      </h1>

      {isPhoneRejected && !verifyError ? (
        <ErrorBanner
          title={INVALID_PHONE_TITLE}
          message={INVALID_PHONE_MESSAGE}
        />
      ) : (
        <ErrorBanner message={verifyError || MISMATCH_MESSAGE} />
      )}

      <div className="mb-3.5 text-[13px] font-semibold uppercase tracking-wide text-[#6c6c6c]">
        Confirm your details
      </div>

      <div className="mb-[26px] flex flex-col gap-4">
        <div className="flex w-full flex-col gap-4">
          <VerifyTextInput
            name="firstName"
            label="First name"
            placeholder="John"
            value={firstName}
            error={firstNameError}
            onChange={(e) => {
              setFirstName(e.target.value);
              setFirstNameError(false);
            }}
            onBlur={() =>
              setFirstNameError(validateName(firstName, true) || false)
            }
          />
          <VerifyTextInput
            name="lastName"
            label="Last name"
            placeholder="Doe"
            value={lastName}
            error={lastNameError}
            onChange={(e) => {
              setLastName(e.target.value);
              setLastNameError(false);
            }}
            onBlur={() =>
              setLastNameError(validateName(lastName, false) || false)
            }
          />
        </div>

        <div className="flex w-full flex-row gap-[14px]">
          <VerifyTextInput
            name="dob"
            label="Date of birth"
            type="dob"
            placeholder="MM/DD/YYYY"
            value={dob}
            error={dobError}
            onChange={(e) => {
              setDob(e.target.value);
              setDobError(false);
            }}
            onBlur={() => setDobError(validateDob(dob) || false)}
          />
          <VerifyTextInput
            name="phone"
            label="Phone number"
            type="phone"
            placeholder="(222) 345-6789"
            value={phone}
            error={phoneError || (isPhoneRejected && !isPhoneRetyped)}
            onChange={(e) => {
              setPhone(e.target.value);
              setPhoneError(false);
              setIsPhoneRetyped(true);
            }}
            onBlur={() => setPhoneError(validatePhone(phone) || false)}
          />
        </div>
      </div>

      <SsnDigitsInput value={ssnValue} onChange={onSsnChange} />

      <EncryptedNote />

      <VerifyButton
        label="Try again"
        loadingLabel="Verifying…"
        iconRight={<LuRotateCw size={19} />}
        disabled={!canSubmit}
        isLoading={isLoading}
        onClick={handleTryAgain}
      />

      <SwitchStateLink
        label="Verify with a photo ID and selfie instead"
        onClick={onUploadInstead}
        disabled={isLoading}
      />
    </>
  );
};

export { MismatchState };
